"use client";

import { useState, type CSSProperties } from "react";
import { motion, useReducedMotion } from "motion/react";
import { cn } from "@/lib/utils";

/*
 * Design Power morph — the marquee proof.
 *
 * ONE representative dealer-site mock (browser chrome → nav, hero banner,
 * vehicle cards, trust bar) whose ENTIRE look morphs when the visitor clicks
 * between named "look" controls. This is the literal demonstration of "design
 * power belongs to the dealer": same structure, radically different identity.
 *
 * Mechanism — scoped CSS variables, not conditional class soup. Each look is a
 * small token bundle (`--lk-*`: surface/ink/accent/banner palette, geometry
 * radius, banner radius, type feel). We set those vars as a dynamic inline
 * `style` on the mock root (the ONE place inline style is legitimate — these
 * are runtime values referenced through CSS vars). Every element inside reads
 * them via Tailwind parenthetical token utilities (`bg-(--lk-surface)`, etc.)
 * and carries `transition-[…]`, so swapping the active look animates the whole
 * frame smoothly in one orchestrated beat.
 *
 * Client-owned because it holds the selected-look state. The headline/copy is
 * rendered by the SERVER `DesignPower` wrapper and passed in as `intro`.
 *
 * Reduced motion: the morph transitions collapse to ~0ms (content is always
 * present and correct; only the tween is suppressed), and the control-swap
 * indicator uses Framer's layout spring which Framer itself disables under
 * reduce.
 */

type Look = {
  id: string;
  name: string;
  tagline: string;
  /** The scoped token bundle painted onto the mock root. */
  vars: Record<string, string>;
  /** Body type feel for the mock — serif vs sans, communicated via font stack. */
  serif: boolean;
};

const LOOKS: Look[] = [
  {
    id: "heritage",
    name: "Heritage",
    tagline: "Warm, trusted, established since.",
    serif: true,
    vars: {
      "--lk-surface": "oklch(0.985 0.008 75)",
      "--lk-surface-2": "oklch(0.955 0.014 75)",
      "--lk-line": "oklch(0.88 0.02 75)",
      "--lk-ink": "oklch(0.28 0.03 50)",
      "--lk-ink-soft": "oklch(0.5 0.03 55)",
      "--lk-accent": "oklch(0.52 0.13 35)",
      "--lk-accent-ink": "oklch(0.98 0.01 75)",
      "--lk-banner": "oklch(0.33 0.04 45)",
      "--lk-banner-ink": "oklch(0.97 0.015 75)",
      "--lk-radius": "12px",
      "--lk-radius-sm": "8px",
      "--lk-banner-radius": "14px",
    },
  },
  {
    id: "bold",
    name: "Bold",
    tagline: "High-contrast, electric, unmissable.",
    serif: false,
    vars: {
      "--lk-surface": "oklch(0.16 0.02 265)",
      "--lk-surface-2": "oklch(0.22 0.03 265)",
      "--lk-line": "oklch(0.32 0.04 265)",
      "--lk-ink": "oklch(0.97 0.005 265)",
      "--lk-ink-soft": "oklch(0.72 0.02 265)",
      "--lk-accent": "oklch(0.72 0.19 250)",
      "--lk-accent-ink": "oklch(0.16 0.02 265)",
      "--lk-banner": "oklch(0.72 0.19 250)",
      "--lk-banner-ink": "oklch(0.15 0.02 265)",
      "--lk-radius": "4px",
      "--lk-radius-sm": "3px",
      "--lk-banner-radius": "4px",
    },
  },
  {
    id: "editorial",
    name: "Editorial",
    tagline: "Generous, refined, magazine-grade.",
    serif: true,
    vars: {
      "--lk-surface": "oklch(0.98 0.01 95)",
      "--lk-surface-2": "oklch(0.955 0.016 95)",
      "--lk-line": "oklch(0.86 0.02 95)",
      "--lk-ink": "oklch(0.24 0.02 90)",
      "--lk-ink-soft": "oklch(0.48 0.02 90)",
      "--lk-accent": "oklch(0.55 0.1 155)",
      "--lk-accent-ink": "oklch(0.98 0.01 95)",
      "--lk-banner": "oklch(0.96 0.03 95)",
      "--lk-banner-ink": "oklch(0.24 0.02 90)",
      "--lk-radius": "2px",
      "--lk-radius-sm": "2px",
      "--lk-banner-radius": "2px",
    },
  },
  {
    id: "minimal",
    name: "Minimal",
    tagline: "Quiet, exact, nothing wasted.",
    serif: false,
    vars: {
      "--lk-surface": "oklch(0.995 0 0)",
      "--lk-surface-2": "oklch(0.965 0 0)",
      "--lk-line": "oklch(0.9 0 0)",
      "--lk-ink": "oklch(0.18 0 0)",
      "--lk-ink-soft": "oklch(0.52 0 0)",
      "--lk-accent": "oklch(0.2 0 0)",
      "--lk-accent-ink": "oklch(0.99 0 0)",
      "--lk-banner": "oklch(0.965 0 0)",
      "--lk-banner-ink": "oklch(0.18 0 0)",
      "--lk-radius": "999px",
      "--lk-radius-sm": "999px",
      "--lk-banner-radius": "20px",
    },
  },
];

const MORPH =
  "transition-[background-color,color,border-color,border-radius] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]";

const SERIF = '"Iowan Old Style", "Palatino Linotype", Palatino, Georgia, serif';
const SANS = "var(--font-onest), ui-sans-serif, system-ui, sans-serif";

export function DesignPowerMorph({ intro }: { intro: React.ReactNode }) {
  const [active, setActive] = useState(0);
  const reduce = useReducedMotion();
  // active is always a valid index (set only by the look buttons); fall back to
  // the first look to satisfy strict index-access typing.
  const look = LOOKS[active] ?? LOOKS[0]!;

  // Dynamic values → legitimate inline style: the scoped look tokens + the
  // body type feel, both consumed through CSS vars by the mock's elements.
  const mockStyle = {
    ...look.vars,
    "--lk-font": look.serif ? SERIF : SANS,
  } as CSSProperties;

  const morph = reduce ? "" : MORPH;

  return (
    <div className="grid grid-cols-1 gap-12 lg:grid-cols-[0.85fr_1.15fr] lg:items-center lg:gap-16">
      {/* ── LEFT: the pitch + the look controls ─────────────────────────── */}
      <div className="flex flex-col">
        {intro}

        <div
          role="group"
          aria-label="Choose a look for the demo site"
          className="mt-9 flex flex-col gap-2"
        >
          {LOOKS.map((l, i) => {
            const selected = i === active;
            return (
              <button
                key={l.id}
                type="button"
                aria-pressed={selected}
                onClick={() => setActive(i)}
                className={cn(
                  "group relative flex items-center gap-4 overflow-hidden rounded-(--radius-lg) border px-4 py-3 text-left transition-colors duration-300",
                  selected
                    ? "border-(--color-brand-accent) bg-(--color-brand-accent-soft)"
                    : "border-(--color-border) bg-(--color-surface-1) hover:border-(--color-border-strong)",
                )}
              >
                {selected && !reduce && (
                  <motion.span
                    layoutId="look-rail"
                    aria-hidden
                    className="absolute inset-y-0 left-0 w-[3px] bg-(--color-brand-accent)"
                    transition={{ type: "spring", stiffness: 420, damping: 34 }}
                  />
                )}
                {selected && reduce && (
                  <span
                    aria-hidden
                    className="absolute inset-y-0 left-0 w-[3px] bg-(--color-brand-accent)"
                  />
                )}

                {/* Mini palette swatch — a live token preview of the look. */}
                <span
                  aria-hidden
                  className="flex shrink-0 overflow-hidden rounded-(--radius-full) border border-(--color-border-strong)"
                >
                  {(["--lk-banner", "--lk-accent", "--lk-surface-2"] as const).map((token) => (
                    <span
                      key={token}
                      className="size-3.5"
                      // Dynamic swatch fill referencing the look's own token.
                      style={{ backgroundColor: `var(${token})`, ...l.vars } as CSSProperties}
                    />
                  ))}
                </span>

                <span className="min-w-0 flex-1">
                  <span className="flex items-baseline gap-2">
                    <span
                      className={cn(
                        "font-mono text-[10px] tabular-nums",
                        selected
                          ? "text-(--color-brand-accent)"
                          : "text-(--color-foreground-faint)",
                      )}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span
                      className={cn(
                        "truncate text-sm font-semibold tracking-tight",
                        selected ? "text-(--color-foreground)" : "text-(--color-foreground-muted)",
                      )}
                    >
                      {l.name}
                    </span>
                  </span>
                  <span className="mt-0.5 block truncate text-xs text-(--color-foreground-subtle)">
                    {l.tagline}
                  </span>
                </span>

                <span
                  className={cn(
                    "shrink-0 font-mono text-[10px] uppercase tracking-[0.18em] transition-opacity duration-300",
                    selected
                      ? "text-(--color-brand-accent) opacity-100"
                      : "text-(--color-foreground-faint) opacity-0 group-hover:opacity-60",
                  )}
                >
                  {selected ? "live" : "apply"}
                </span>
              </button>
            );
          })}

          <p className="mt-3 max-w-sm font-mono text-[11px] leading-relaxed tracking-wide text-(--color-foreground-faint)">
            {"// "}same content, same speed — every pixel of the look is yours to set.
          </p>
        </div>
      </div>

      {/* ── RIGHT: the single mock that morphs ──────────────────────────── */}
      <div className="relative">
        {/* Soft accent bloom keyed off the live look for atmosphere. */}
        <div
          aria-hidden
          className={cn(
            "pointer-events-none absolute -inset-6 -z-10 rounded-(--radius-2xl) opacity-50 blur-3xl transition-opacity duration-700",
            reduce && "transition-none",
          )}
          style={
            {
              background: `radial-gradient(60% 60% at 70% 20%, var(--lk-accent), transparent 70%)`,
              ...look.vars,
            } as CSSProperties
          }
        />

        {/* Browser chrome (neutral, fixed) framing the morphing site. */}
        <div className="overflow-hidden rounded-(--radius-xl) border border-(--color-border-strong) bg-(--color-surface-1) shadow-(--shadow-card)">
          {/* Title bar — stays neutral; the URL pill reports the live look. */}
          <div className="flex items-center gap-2 border-b border-(--color-border) bg-(--color-surface-2) px-4 py-3">
            <span className="size-2.5 rounded-(--radius-full) bg-(--color-border-strong)" />
            <span className="size-2.5 rounded-(--radius-full) bg-(--color-border-strong)" />
            <span className="size-2.5 rounded-(--radius-full) bg-(--color-border-strong)" />
            <div className="ml-3 flex-1 rounded-(--radius-full) bg-(--color-surface-3) px-3 py-1">
              <span className="font-mono text-[10px] tracking-wide text-(--color-foreground-subtle)">
                yourdealership.com
              </span>
            </div>
            <span
              key={look.id}
              className="font-mono text-[10px] uppercase tracking-[0.18em] text-(--color-brand-accent)"
            >
              ● {look.name}
            </span>
          </div>

          {/* THE SITE — every surface reads the scoped look tokens + morphs. */}
          <div
            style={mockStyle}
            className={cn(
              "flex aspect-[4/5] flex-col gap-3 p-5 [font-family:var(--lk-font)] sm:aspect-[4/3.3]",
              "bg-(--lk-surface) text-(--lk-ink)",
              morph,
            )}
          >
            {/* Nav */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <span className={cn("size-6 bg-(--lk-accent) rounded-(--lk-radius-sm)", morph)} />
                <span className="text-[15px] font-bold tracking-tight">Vanguard Auto</span>
              </div>
              <div className="flex items-center gap-4 text-[11px] text-(--lk-ink-soft)">
                <span className="hidden sm:inline">Inventory</span>
                <span className="hidden sm:inline">Financing</span>
                <span className="hidden sm:inline">About</span>
                <span
                  className={cn(
                    "bg-(--lk-accent) text-(--lk-accent-ink) rounded-(--lk-radius) px-3 py-1.5 text-[11px] font-semibold",
                    morph,
                  )}
                >
                  Contact
                </span>
              </div>
            </div>

            {/* Hero banner */}
            <div
              className={cn(
                "relative flex min-h-[40%] flex-col justify-center gap-2 overflow-hidden rounded-(--lk-banner-radius) bg-(--lk-banner) p-5 text-(--lk-banner-ink)",
                morph,
              )}
            >
              <span
                aria-hidden
                className={cn(
                  "absolute -right-10 -top-12 size-40 rounded-(--radius-full) bg-(--lk-accent) opacity-25 blur-2xl",
                  morph,
                )}
              />
              <span className="relative text-[10px] font-medium uppercase tracking-[0.2em] opacity-70">
                Certified Pre-Owned
              </span>
              <span className="relative max-w-[80%] text-[22px] font-bold leading-[1.05] tracking-tight">
                Drive home today.
              </span>
              <span
                className={cn(
                  "relative mt-1 w-fit bg-(--lk-accent) text-(--lk-accent-ink) rounded-(--lk-radius) px-4 py-1.5 text-[11px] font-semibold",
                  morph,
                )}
              >
                Browse 240 vehicles
              </span>
            </div>

            {/* Vehicle cards */}
            <div className="grid grid-cols-3 gap-2.5">
              {[
                { name: "2023 RANGER", price: "$38,400" },
                { name: "2022 CIVIC", price: "$24,900" },
                { name: "2024 MODEL Y", price: "$46,200" },
              ].map((v) => (
                <div
                  key={v.name}
                  className={cn(
                    "overflow-hidden rounded-(--lk-radius-sm) border border-(--lk-line) bg-(--lk-surface-2)",
                    morph,
                  )}
                >
                  <div className={cn("h-10 bg-(--lk-line) opacity-70", morph)} />
                  <div className="flex flex-col gap-1 p-2">
                    <span className="font-mono text-[8px] tracking-wide text-(--lk-ink-soft)">
                      {v.name}
                    </span>
                    <span className="text-[11px] font-bold text-(--lk-accent)">{v.price}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Trust bar */}
            <div
              className={cn(
                "mt-auto flex items-center justify-between rounded-(--lk-radius-sm) border border-(--lk-line) bg-(--lk-surface-2) px-3 py-2.5",
                morph,
              )}
            >
              <div className="flex items-center gap-2">
                <span className={cn("size-2 rounded-(--radius-full) bg-(--lk-accent)", morph)} />
                <span className="text-[10px] font-medium text-(--lk-ink-soft)">
                  4.9 ★ · 1,200+ happy drivers
                </span>
              </div>
              <span
                className={cn(
                  "border border-(--lk-line) text-(--lk-ink) rounded-(--lk-radius) px-3 py-1 text-[10px] font-semibold",
                  morph,
                )}
              >
                Get pre-approved
              </span>
            </div>
          </div>
        </div>

        <p className="mt-4 text-center font-mono text-[11px] uppercase tracking-[0.16em] text-(--color-foreground-subtle)">
          One codebase · {LOOKS.length} looks · zero migrations
        </p>
      </div>
    </div>
  );
}
